import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ContentService } from '../services/content.service';
import { UserStateService } from '@app/services/user-state/user-state.service';
import { ContentGroup, Content } from '@app/utils/interfaces/content.interface';
import { TeaserSectionData } from '@app/utils/interfaces/teaser.interface';
import { OfferSectionData } from '@app/utils/interfaces/offer.interface';
import {
  ContentGlobalEditorResult,
  MainPageSectionRow,
} from '@app/utils/interfaces/editor-interface';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { CONTENT_VIEW_MODAL_CONFIGS } from '@app/utils/constants/modal.constants';
import { IconName } from '@app/utils/enums/icon-enum';
import { LargeModalComponent } from './components/large-modal/large-modal.component';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import { ContentGlobalEditorComponent } from './components/content-global-editor/content-global-editor.component';

@Component({
  selector: 'app-settings-content',
  standalone: true,
  imports: [TranslateModule, DatePipe, IconComponent, MatMenuModule, MatButtonModule],
  templateUrl: './settings-content.component.html',
  styleUrl: './settings-content.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsContentComponent {
  protected contents = signal<ContentGroup[]>([]);
  protected teaserSection = signal<TeaserSectionData | null>(null);
  protected offersSection = signal<OfferSectionData | null>(null);
  protected iconNames = IconName;
  protected readonly jsonPath = 'shop-settings.content.content-tables.';

  protected mainPageContents = computed((): MainPageSectionRow[] => {
    const homePageContents =
      this.contents().find((group: ContentGroup) => group.groupName === 'home-page')?.contents ??
      [];

    const regularRows: MainPageSectionRow[] = homePageContents.map((content: Content) => ({
      id: String(content.id),
      sectionType: content.sectionType,
      isVisible: content.isVisible,
      lastUpdated: content.lastUpdated ?? null,
      updatedBy: content.updatedBy ?? '',
      source: 'content',
      rawData: content,
    }));

    const teaser = this.teaserSection();
    const offers = this.offersSection();

    const hasExistingTeaser = regularRows.some(
      (row: MainPageSectionRow) => row.sectionType === 'teaser',
    );
    const hasExistingOffers = regularRows.some(
      (row: MainPageSectionRow) => row.sectionType === 'offers',
    );

    const teaserRows: MainPageSectionRow[] =
      teaser && !hasExistingTeaser
        ? [
            {
              id: 'teaser-section',
              sectionType: 'teaser',
              isVisible: teaser.isVisible,
              lastUpdated: null,
              updatedBy: '-',
              source: 'teaser',
              rawData: teaser,
            },
          ]
        : [];

    const offersRows: MainPageSectionRow[] =
      offers && !hasExistingOffers
        ? [
            {
              id: 'offers-section',
              sectionType: 'offers',
              isVisible: offers.isVisible,
              lastUpdated: null,
              updatedBy: '-',
              source: 'offers',
              rawData: offers,
            },
          ]
        : [];

    const desiredOrder = ['categories', 'teaser', 'location', 'new-products', 'offers'];

    return [...regularRows, ...teaserRows, ...offersRows].sort(
      (a: MainPageSectionRow, b: MainPageSectionRow) =>
        desiredOrder.indexOf(a.sectionType) - desiredOrder.indexOf(b.sectionType),
    );
  });

  protected staticPageContents = computed((): Content[] => {
    return (
      this.contents().find((group: ContentGroup) => group.groupName === 'footer')?.contents ?? []
    );
  });

  private readonly contentService: ContentService = inject(ContentService);
  private readonly userStateService: UserStateService = inject(UserStateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly matDialog = inject(MatDialog);

  constructor() {
    effect(
      () => {
        const userData = this.userStateService.userData() as {
          data?: { shopId?: string | number };
        } | null;

        const shopId = userData?.data?.shopId?.toString();

        if (!shopId) {
          return;
        }

        this.loadAllData(shopId);
      },
      {
        allowSignalWrites: true,
      },
    );
  }

  protected getContentPreview(content: string | null | undefined): string {
    if (!content || !content.trim()) {
      return '<Empty>';
    }

    const normalizedContent = content
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return normalizedContent || '<Empty>';
  }

  protected openEditContent(item: MainPageSectionRow | Content): void {
    if (this.isMainPageSectionRow(item)) {
      if (item.source === 'content') {
        this.openGenericEditModal(item.rawData as Content);
        return;
      }

      if (item.source === 'teaser') {
        this.openTeaserEditor(item.rawData as TeaserSectionData);
        return;
      }

      if (item.source === 'offers') {
        this.openOffersEditor(item.rawData as OfferSectionData);
        return;
      }

      return;
    }

    this.openGenericEditModal(item);
  }

  protected openEditCategoriesModal(item: Content): void {
    const dialogRef = this.matDialog.open(ContentGlobalEditorComponent, {
      width: '960px',
      maxWidth: '95vw',
      disableClose: true,
      data: {
        type: 'categories',
        sectionLabel: item.sectionType,
        payload: structuredClone(item),
      },
    });

    dialogRef.afterClosed().subscribe((result: ContentGlobalEditorResult | undefined) => {
      if (!result) {
        return;
      }

      this.applyEditorResult(result);

      const userData = this.userStateService.userData() as {
        data?: { shopId?: string | number };
      } | null;

      const shopId = userData?.data?.shopId?.toString();

      if (!shopId) {
        return;
      }

      this.loadAllData(shopId);
    });
  }

  protected openViewContent(item: MainPageSectionRow | Content): void {
    if (this.isMainPageSectionRow(item)) {
      if (item.source === 'content') {
        this.openGenericViewModal(item.rawData as Content);
        return;
      }

      if (item.source === 'teaser') {
        const teaser = item.rawData as TeaserSectionData;

        this.matDialog.open(LargeModalComponent, {
          ...CONTENT_VIEW_MODAL_CONFIGS,
          data: {
            titleKey: 'shop-settings.content.table.section-type.teaser',
            text: `
              Heading: ${teaser.heading || '-'}
              Additional text: ${teaser.additionalText || '-'}
              Link: ${teaser.link || '-'}
              Image: ${teaser.teaserImageUrl || '-'}
              Heading alignment: ${teaser.headingAlignment}
              Additional text alignment: ${teaser.additionalTextAlignment}
              Heading color: ${teaser.headingColor || '-'}
              Additional text color: ${teaser.additionalTextColor || '-'}
            `.trim(),
          },
        });

        return;
      }

      if (item.source === 'offers') {
        this.matDialog.open(LargeModalComponent, {
          ...CONTENT_VIEW_MODAL_CONFIGS,
          data: {
            titleKey: 'shop-settings.content.table.section-type.offers',
            text: JSON.stringify(item.rawData ?? {}, null, 2),
          },
        });

        return;
      }

      return;
    }

    this.openGenericViewModal(item);
  }

  private isMainPageSectionRow(item: MainPageSectionRow | Content): item is MainPageSectionRow {
    return 'source' in item;
  }

  private openGenericViewModal(content: Content): void {
    this.matDialog.open(LargeModalComponent, {
      ...CONTENT_VIEW_MODAL_CONFIGS,
      data: {
        titleKey: `shop-settings.content.table.section-type.${content.sectionType}`,
        text: content.content,
      },
    });
  }

  private openGenericEditModal(content: Content): void {
    const dialogRef = this.matDialog.open(ContentGlobalEditorComponent, {
      width: '960px',
      maxWidth: '95vw',
      disableClose: true,
      data: {
        type: 'content',
        sectionLabel: content.sectionType,
        payload: structuredClone(content),
      },
    });

    dialogRef.afterClosed().subscribe((result: ContentGlobalEditorResult | undefined) => {
      if (!result) {
        return;
      }

      this.applyEditorResult(result);

      const userData = this.userStateService.userData() as {
        data?: { shopId?: string | number };
      } | null;

      const shopId = userData?.data?.shopId?.toString();

      if (!shopId) {
        return;
      }

      this.loadAllData(shopId);
    });
  }

  private openTeaserEditor(teaser: TeaserSectionData): void {
    const dialogRef = this.matDialog.open(ContentGlobalEditorComponent, {
      width: '960px',
      maxWidth: '95vw',
      disableClose: true,
      data: {
        type: 'teaser',
        sectionLabel: 'Teaser',
        payload: structuredClone(teaser),
      },
    });

    dialogRef.afterClosed().subscribe((result: ContentGlobalEditorResult | undefined) => {
      if (!result) {
        return;
      }

      this.applyEditorResult(result);

      const userData = this.userStateService.userData() as {
        data?: { shopId?: string | number };
      } | null;

      const shopId = userData?.data?.shopId?.toString();

      if (!shopId) {
        return;
      }

      this.loadAllData(shopId);
    });
  }

  private openOffersEditor(offers: OfferSectionData): void {
    const dialogRef = this.matDialog.open(ContentGlobalEditorComponent, {
      width: '960px',
      maxWidth: '95vw',
      disableClose: true,
      data: {
        type: 'offers',
        sectionLabel: 'Offers',
        payload: structuredClone(offers),
      },
    });

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private loadAllData(shopId: string): void {
    forkJoin({
      contents: this.contentService.getContents(shopId),
      teaser: this.contentService.getTeaserSection().pipe(catchError(() => of(null))),
      offers: this.contentService.getOffersSection().pipe(catchError(() => of(null))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ contents, teaser, offers }) => {
          this.contents.set(contents);
          this.teaserSection.set(teaser);
          this.offersSection.set(offers);
        },
        error: () => {
          this.openErrorModal();
        },
      });
  }

  private applyEditorResult(result: ContentGlobalEditorResult): void {
    if (result.type === 'content') {
      const updatedContent = result.payload as Content;

      this.contents.update((groups: ContentGroup[]) =>
        groups.map((group: ContentGroup) => ({
          ...group,
          contents: group.contents.map((content: Content) =>
            String(content.id) === String(updatedContent.id) ? updatedContent : content,
          ),
        })),
      );

      return;
    }

    if (result.type === 'teaser') {
      this.teaserSection.set(result.payload as TeaserSectionData);
      return;
    }

    if (result.type === 'offers') {
      this.offersSection.set(result.payload as OfferSectionData);
    }
  }

  private openErrorModal(): void {
    this.matDialog.open(StatusModalComponent, {
      data: {
        isSuccess: false,
        title: 'shop-settings.content.table.errors.title',
        description: 'shop-settings.content.table.errors.description',
      },
    });
  }
}
